import PropTypes from "prop-types";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { COMMON_AMOUNT_CONFIG } from "../../utils/zhl-calculator.esm.js";
const FieldConfig = {
  annualIncome: {
    name: "annualIncome",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  minCreditCardPayments: {
    name: "minCreditCardPayments",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  carLoanPayments: {
    name: "carLoanPayments",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  studentLoanPayments: {
    name: "studentLoanPayments",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  alimonySupportPayments: {
    name: "alimonySupportPayments",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  secondaryHomeExpenses: {
    name: "secondaryHomeExpenses",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  otherDebtPayments: {
    name: "otherDebtPayments",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  }
};
const DEFAULT_VALUES_SHAPE = {
  annualIncome: PropTypes.number,
  minCreditCardPayments: PropTypes.number,
  carLoanPayments: PropTypes.number,
  studentLoanPayments: PropTypes.number,
  alimonySupportPayments: PropTypes.number,
  secondaryHomeExpenses: PropTypes.number,
  otherDebtPayments: PropTypes.number
};
const RESULT_COPY = {
  VERY_GOOD: "Your DTI is very good.",
  GOOD: "Your DTI is good.",
  OK: "Your DTI is OK.",
  OVER: "Your DTI is over the limit."
};
function useLastValidDTIValues(form) {
  return useLastValidValues(form, getDTIValues);
}
function getDTIValues(lastValidFields) {
  const monthlyIncome = lastValidFields.annualIncome / 12;
  const debtsOut = lastValidFields.minCreditCardPayments + lastValidFields.carLoanPayments + lastValidFields.studentLoanPayments + lastValidFields.alimonySupportPayments + lastValidFields.secondaryHomeExpenses + lastValidFields.otherDebtPayments;
  const dtiOut = debtsOut / monthlyIncome * 100;
  const remainingOut = monthlyIncome - debtsOut;
  const calculations = {
    monthlyIncome,
    debtsOut,
    dtiOut,
    remainingOut
  };
  const values = {
    fields: lastValidFields,
    calculations
  };
  return values;
}
const getDefaultMortgagePayment = (totalMonthlyDebts, monthlyIncome) => {
  const DTIratio = totalMonthlyDebts / monthlyIncome * 100;
  if (DTIratio <= 30) {
    const defaultMortgagePayment = 0.36 * monthlyIncome - totalMonthlyDebts;
    return Math.round(defaultMortgagePayment);
  } else if (DTIratio > 30 && DTIratio <= 50) {
    const defaultMortgagePayment = 0.5 * monthlyIncome - totalMonthlyDebts;
    return Math.round(defaultMortgagePayment);
  }
  return 0;
};
const getRemainingMonthlyPayment = (monthlyIncome, debtsOut, mortgagePayment) => monthlyIncome - debtsOut - mortgagePayment;
const getDTIMessageParts = (value, expanded) => {
  const {
    VERY_GOOD,
    GOOD,
    OK,
    OVER
  } = RESULT_COPY;
  if (isNaN(value) || value <= 20) {
    return {
      result: VERY_GOOD,
      description: "Having a DTI ratio of 36% or less is considered ideal, and anything under 20% is excellent."
    };
  }
  if (value > 20 && value <= 36) {
    return {
      result: GOOD,
      description: "Having a DTI ratio of 36% or less is considered ideal."
    };
  }
  if (value > 36 && value <= 50) {
    return {
      result: OK,
      description: `It's under the 50% limit, but having a DTI ratio of 36% or less is considered ideal. Paying down debt or${expanded ? " increasing your income can help improve your DTI ratio." : ""}`
    };
  }
  return {
    result: OVER,
    description: `In most cases, 50% is the highest debt-to-income that lenders will allow. Paying down debt or${expanded ? " increasing your income can help improve your DTI ratio." : ""}`
  };
};
export {
  DEFAULT_VALUES_SHAPE,
  FieldConfig,
  RESULT_COPY,
  getDTIMessageParts,
  getDefaultMortgagePayment,
  getRemainingMonthlyPayment,
  useLastValidDTIValues
};
